import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin", chairman: "Chairman", engineering_head: "Engineering Head",
  budget_controller: "Budget Controller", site_engineer: "Site Engineer",
};

export default function Users() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/users").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0D3B66] tracking-tight">Users</h1>
          <p className="mt-1.5 text-slate-500">Manage team accounts and roles.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="rounded-full bg-[#F97316] hover:bg-[#EA6A0E]"><Plus className="h-4 w-4 mr-1.5" /> New user</Button>
          </DialogTrigger>
          <NewUserDialog onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>
      {msg && <div data-testid="user-msg" className="text-sm text-slate-600">{msg}</div>}

      <Card className="border-slate-200 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
          <div className="col-span-3">Name</div><div className="col-span-4">Email</div><div className="col-span-3">Role</div><div className="col-span-2 text-right"></div>
        </div>
        {items.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-slate-100 items-center text-sm">
            <div className="col-span-3 text-slate-800 font-medium">{u.name}</div>
            <div className="col-span-4 text-slate-500 truncate">{u.email}</div>
            <div className="col-span-3">
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs bg-[#0D3B66]/10 text-[#0D3B66] font-medium">{ROLE_LABEL[u.role] || u.role}</span>
            </div>
            <div className="col-span-2 text-right">
              <button data-testid={`user-delete-${u.id}`} onClick={() => del(u)} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function NewUserDialog({ onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("site_engineer");
  const [password, setPassword] = useState("Password@123");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setErr("");
    try { await api.post("/users", { email, name, role, password, project_ids: [] }); onDone(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent data-testid="new-user-dialog">
      <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div className="space-y-1.5"><Label>Name</Label><Input data-testid="user-name-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input data-testid="user-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Password</Label><Input data-testid="user-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        {err && <div className="text-sm text-rose-700">{err}</div>}
      </div>
      <DialogFooter>
        <Button data-testid="user-save-btn" onClick={save} disabled={busy || !email || !name} className="bg-[#0D3B66] hover:bg-[#114B82]">Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}
